import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import topicIcon1 from '../../assets/images/topics/1.svg';
import topicIcon2 from '../../assets/images/topics/2.png';
import topicIcon3 from '../../assets/images/topics/3.svg';
import topicIcon4 from '../../assets/images/topics/4.svg';
import './institution-dashboard-responsive.css';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_BASE = process.env.REACT_APP_STUDENT_DASHBOARD_API_BASE || `${API_BASE_URL}/api/student-app`;
const INSTITUTION_TEST_HISTORY_API = process.env.REACT_APP_INSTITUTION_TEST_HISTORY_API || `${API_BASE_URL}/api/admin-app/institution-test-history/`;
const ASSIGN_QUIZ_API = process.env.REACT_APP_ASSIGN_QUIZ_API || `${API_BASE_URL}/api/admin-app/assign-quiz/`;
const STUDENT_ASSIGNED_QUIZZES_API = process.env.REACT_APP_STUDENT_ASSIGNED_QUIZZES_API || `${API_BASE_URL}/api/admin-app/student-assigned-quizzes/`;
const UNASSIGN_QUIZ_API = process.env.REACT_APP_UNASSIGN_QUIZ_API || `${API_BASE_URL}/api/admin-app/unassign-quiz/`;
const COLLEGE_AUTO_APPROVE_API = process.env.REACT_APP_COLLEGE_AUTO_APPROVE_API || `${API_BASE_URL}/api/admin-app/college-auto-approve/`;

const InstitutionDashboardMain = () => {
    const [summary, setSummary] = useState({
        total_requests: 0,
        pending_requests: 0,
        approved_requests: 0,
        rejected_requests: 0,
    });
    const [profiles, setProfiles] = useState([]);
    const [startedTests, setStartedTests] = useState([]);
    const [assignedQuizzes, setAssignedQuizzes] = useState([]);
    const [assignedLoading, setAssignedLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [testsLoading, setTestsLoading] = useState(true);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [assignMessage, setAssignMessage] = useState('');
    const [unassignMessage, setUnassignMessage] = useState('');
    const [autoApproving, setAutoApproving] = useState(false);
    const [autoApproveEnabled, setAutoApproveEnabled] = useState(true);
    const [togglingAutoApprove, setTogglingAutoApprove] = useState(false);

    const statCards = useMemo(
        () => [
            {
                icon: topicIcon1,
                title: 'Total Requests',
                value: summary.total_requests,
                detail: 'All student profile submissions',
            },
            {
                icon: topicIcon2,
                title: 'Pending',
                value: summary.pending_requests,
                detail: 'Awaiting institution decision',
            },
            {
                icon: topicIcon3,
                title: 'Approved',
                value: summary.approved_requests,
                detail: 'Profiles accepted by institution',
            },
            {
                icon: topicIcon4,
                title: 'Rejected',
                value: summary.rejected_requests,
                detail: 'Profiles declined by institution',
            },
        ],
        [summary]
    );

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setErrorMessage('');

            const collegeCode = (
                localStorage.getItem('guest_college_code') ||
                sessionStorage.getItem('guest_college_code') ||
                ''
            ).trim();

            if (!collegeCode) {
                setSummary({
                    total_requests: 0,
                    pending_requests: 0,
                    approved_requests: 0,
                    rejected_requests: 0,
                });
                setProfiles([]);
                setErrorMessage('College session not found. Please log in again.');
                return;
            }

            const query = `?college_code=${encodeURIComponent(collegeCode)}`;

            const [summaryResponse, profilesResponse] = await Promise.all([
                fetch(`${API_BASE}/dashboard-summary/${query}`),
                fetch(`${API_BASE}/profiles/${query}`),
            ]);

            if (!summaryResponse.ok || !profilesResponse.ok) {
                throw new Error('Unable to load student profile data.');
            }

            const summaryData = await summaryResponse.json();
            const profileData = await profilesResponse.json();

            setSummary(summaryData);
            setProfiles(Array.isArray(profileData) ? profileData : []);
        } catch (error) {
            setErrorMessage('Failed to fetch student profiles. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchCollegeProfile = async () => {
        try {
            const collegeCode = (
                localStorage.getItem('guest_college_code') ||
                sessionStorage.getItem('guest_college_code') ||
                ''
            ).trim();

            if (!collegeCode) {
                return;
            }

            const response = await fetch(`${COLLEGE_AUTO_APPROVE_API}${encodeURIComponent(collegeCode)}/`, {
                method: 'GET',
            });

            if (!response.ok) {
                return;
            }

            const data = await response.json();
            if (data?.college) {
                setAutoApproveEnabled(data.college.auto_approve_students !== false);
            }
        } catch (error) {
            // Silently fail - college profile fetch is non-critical
        }
    };

    const fetchStartedTests = async () => {
        try {
            setTestsLoading(true);

            const response = await fetch(INSTITUTION_TEST_HISTORY_API, {
                method: 'GET',
            });

            if (!response.ok) {
                throw new Error('Unable to load started tests.');
            }

            const data = await response.json();
            setStartedTests(Array.isArray(data) ? data : []);
        } catch (error) {
            setStartedTests([]);
            setErrorMessage('Failed to fetch test history. Please try again.');
        } finally {
            setTestsLoading(false);
        }
    };

    const fetchAssignedQuizzes = async () => {
        const collegeCode = (
            localStorage.getItem('guest_college_code') ||
            sessionStorage.getItem('guest_college_code') ||
            ''
        ).trim();

        if (!collegeCode) {
            setAssignedQuizzes([]);
            setAssignedLoading(false);
            return;
        }

        try {
            setAssignedLoading(true);
            const response = await fetch(`${STUDENT_ASSIGNED_QUIZZES_API}?college_code=${encodeURIComponent(collegeCode)}`);
            if (!response.ok) {
                setAssignedQuizzes([]);
                return;
            }
            const data = await response.json();
            setAssignedQuizzes(Array.isArray(data) ? data : []);
        } catch (error) {
            setAssignedQuizzes([]);
        } finally {
            setAssignedLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        fetchStartedTests();
        fetchAssignedQuizzes();
        fetchCollegeProfile();
    }, []);

    const handleStatusUpdate = async (profileId, nextStatus) => {
        try {
            setActionLoadingId(profileId);
            setErrorMessage('');

            const response = await fetch(`${API_BASE}/profiles/${profileId}/status/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ confirmation_status: nextStatus }),
            });

            if (!response.ok) {
                throw new Error('Status update failed.');
            }

            await fetchDashboardData();
        } catch (error) {
            setErrorMessage('Unable to update profile status right now.');
        } finally {
            setActionLoadingId(null);
        }
    };

    const getBadgeClass = (status) => {
        if (status === 'approved') return 'badge bg-success';
        if (status === 'rejected') return 'badge bg-danger';
        return 'badge bg-warning text-dark';
    };

    const handleAssignQuiz = async (testId) => {
        const collegeCode = (
            localStorage.getItem('guest_college_code') ||
            sessionStorage.getItem('guest_college_code') ||
            ''
        ).trim();

        if (!collegeCode) {
            setErrorMessage('College session not found. Please log in again.');
            return;
        }

        try {
            setActionLoadingId(testId);
            setErrorMessage('');
            setAssignMessage('');
            setUnassignMessage('');

            const response = await fetch(ASSIGN_QUIZ_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    test_history_id: testId,
                    college_code: collegeCode,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                setErrorMessage(data?.detail || data?.error || 'Unable to assign quiz right now.');
                return;
            }

            setAssignMessage(`Quiz #${testId} assigned successfully to ${collegeCode}.`);
            await fetchAssignedQuizzes();
        } catch (error) {
            setErrorMessage('Unable to assign quiz right now.');
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleAutoApprovePending = async () => {
        const pendingProfiles = (profiles || []).filter((p) => p.confirmation_status === 'pending');

        if (pendingProfiles.length === 0) {
            setAssignMessage('No pending student requests to approve.');
            setUnassignMessage('');
            setErrorMessage('');
            return;
        }

        try {
            setAutoApproving(true);
            setErrorMessage('');
            setAssignMessage('');
            setUnassignMessage('');

            await Promise.all(
                pendingProfiles.map((profile) =>
                    fetch(`${API_BASE}/profiles/${profile.id}/status/`, {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ confirmation_status: 'approved' }),
                    }).then((response) => {
                        if (!response.ok) {
                            throw new Error('Status update failed.');
                        }
                        return response;
                    })
                )
            );

            setAssignMessage(`Auto approval complete. Approved ${pendingProfiles.length} student request(s).`);
            await fetchDashboardData();
        } catch (error) {
            setErrorMessage('Unable to auto-approve pending students right now.');
        } finally {
            setAutoApproving(false);
        }
    };

    const handleUnassignQuiz = async (testId) => {
        const collegeCode = (
            localStorage.getItem('guest_college_code') ||
            sessionStorage.getItem('guest_college_code') ||
            ''
        ).trim();

        if (!collegeCode) {
            setErrorMessage('College session not found. Please log in again.');
            return;
        }

        try {
            setActionLoadingId(testId);
            setErrorMessage('');
            setAssignMessage('');
            setUnassignMessage('');

            const response = await fetch(UNASSIGN_QUIZ_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    test_history_id: testId,
                    college_code: collegeCode,
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                setErrorMessage(data?.detail || data?.error || 'Unable to delete assigned quiz right now.');
                return;
            }

            setUnassignMessage(`Quiz #${testId} deleted successfully from ${collegeCode}.`);
            await fetchAssignedQuizzes();
        } catch (error) {
            setErrorMessage('Unable to delete assigned quiz right now.');
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleToggleAutoApprove = async () => {
        const collegeCode = (
            localStorage.getItem('guest_college_code') ||
            sessionStorage.getItem('guest_college_code') ||
            ''
        ).trim();

        if (!collegeCode) {
            setErrorMessage('College session not found. Please log in again.');
            return;
        }

        const nextAutoApproveValue = !autoApproveEnabled;

        try {
            setTogglingAutoApprove(true);
            setErrorMessage('');
            setAssignMessage('');

            const response = await fetch(`${COLLEGE_AUTO_APPROVE_API}${encodeURIComponent(collegeCode)}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ auto_approve_students: nextAutoApproveValue }),
            });

            if (!response.ok) {
                throw new Error('Failed to toggle auto-approval setting.');
            }

            const data = await response.json();
            setAutoApproveEnabled(data?.college?.auto_approve_students === true);
            setAssignMessage(`Auto-approval ${nextAutoApproveValue ? 'enabled' : 'disabled'} for new student registrations.`);
        } catch (error) {
            setErrorMessage('Unable to toggle auto-approval setting right now.');
        } finally {
            setTogglingAutoApprove(false);
        }
    };

    const assignedTestIds = useMemo(() => new Set((assignedQuizzes || []).map((q) => q.test_id)), [assignedQuizzes]);
    const availableTests = useMemo(
        () => (startedTests || []).filter((t) => !assignedTestIds.has(t.id)),
        [startedTests, assignedTestIds]
    );

    return (
        <>
            <div className="react_populars_topics pt---70 pb---70 institution-dashboard">
                <div className="container">
                    <div className="react__title__section react__title__section2">
                        <div className="row align-v institution-dashboard-title-row">
                            <div className="col-md-8">
                                <h2 className="react__tittle">Institution Performance Dashboard</h2>
                                <p className="react__subtitle">Live student profile pipeline with approve and reject controls.</p>
                            </div>
                            <div className="col-md-4 text-right institution-dashboard-refresh-wrap">
                                <Link
                                    to="/dashboard/institution/test-history?role=institution"
                                    className="react-btn-primary institution-dashboard-refresh-btn me-2"
                                    style={{ display: 'inline-block', textDecoration: 'none' }}
                                >
                                    Attended History
                                </Link>
                                <button
                                    className="react-btn-primary institution-dashboard-refresh-btn"
                                    style={{ display: 'inline-block' }}
                                    onClick={() => {
                                        fetchDashboardData();
                                        fetchStartedTests();
                                        fetchAssignedQuizzes();
                                    }}
                                    disabled={loading || testsLoading}
                                >
                                    {loading || testsLoading ? 'Refreshing...' : 'Refresh Data'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {errorMessage && (
                        <div className="alert alert-danger mt-4" role="alert">
                            {errorMessage}
                        </div>
                    )}

                    {assignMessage && (
                        <div className="alert alert-success mt-4" role="alert">
                            {assignMessage}
                        </div>
                    )}

                    {unassignMessage && (
                        <div className="alert alert-success mt-4" role="alert">
                            {unassignMessage}
                        </div>
                    )}

                    <div className="row pt---30 institution-dashboard-stat-grid">
                        {statCards.map((card) => (
                            <div className="col-6 col-md-6 col-lg-3" key={card.title}>
                                <div className="item__inner">
                                    <div className="icon">
                                        <img src={card.icon} alt={card.title} />
                                    </div>
                                    <div className="react-content">
                                        <h3 className="react-title">{card.title}</h3>
                                        <h4>{card.value}</h4>
                                        <p>{card.detail}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="container mt-4 mb-5">
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' }}>
                    <div className="card-body p-4 text-white">
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <h3 className="text-white fw-bold mb-1">Live Monitoring Hub</h3>
                                <p className="text-white-50 mb-0">View real-time webcam feeds of students currently taking exams.</p>
                            </div>
                            <div className="d-none d-md-block">
                                <i className="fas fa-video fa-3x opacity-25"></i>
                            </div>
                        </div>
                        
                        <div className="mt-4 row g-3">
                            {assignedQuizzes.length === 0 ? (
                                <div className="col-12 text-center py-4 bg-white bg-opacity-10 rounded-3">
                                    <span className="small">No assigned quizzes found. Assign a quiz from the "Assigned Quizzes" section below to start monitoring.</span>
                                </div>
                            ) : (
                                assignedQuizzes.map(quiz => (
                                    <div className="col-md-4 col-lg-3" key={quiz.id}>
                                        <Link 
                                            to={`/dashboard/monitoring/${quiz.test_id}`}
                                            className="btn btn-light w-100 py-3 shadow-sm d-flex flex-column align-items-center justify-content-center hover-scale"
                                            style={{ transition: 'all 0.2s ease', border: 'none', borderRadius: '12px' }}
                                        >
                                            <span className="fw-bold text-primary">Monitor Quiz #{quiz.test_id}</span>
                                            <span className="small text-muted mt-1">{quiz.selected_question_count} Questions</span>
                                        </Link>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                .hover-scale:hover {
                    transform: scale(1.03);
                    background-color: #f8fafc !important;
                }
            `}} />

            <div className="accordion__area pb---120 institution-dashboard-content">
                <div className="container">
                    <div className="row g-4">
                        <div className="col-lg-12">
                            <div className="login-right-form institution-dashboard-table-card" style={{ height: '100%' }}>
                                <div className="login-top">
                                    <h3>Student Profile Requests</h3>
                                    <p>Fetched from StudentProfile API with real-time approval workflow.</p>
                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                        <div>
                                            <h3>Student Profile Requests</h3>
                                        </div>
                                        <div className="d-flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-success btn-sm"
                                                onClick={handleAutoApprovePending}
                                                disabled={loading || autoApproving || Number(summary.pending_requests || 0) === 0}
                                            >
                                                {autoApproving ? 'Auto Approving...' : `Auto Approve Pending (${Number(summary.pending_requests || 0)})`}
                                            </button>
                                            <button
                                                type="button"
                                                className={`institution-dashboard-toggle ${autoApproveEnabled ? 'is-on' : 'is-off'}`}
                                                onClick={handleToggleAutoApprove}
                                                disabled={togglingAutoApprove}
                                                aria-pressed={autoApproveEnabled}
                                            >
                                                <span className="institution-dashboard-toggle-track" aria-hidden="true">
                                                    <span className="institution-dashboard-toggle-thumb" />
                                                </span>
                                                <span>{togglingAutoApprove ? 'Updating...' : `Auto-Approve ${autoApproveEnabled ? 'ON' : 'OFF'}`}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="table-responsive institution-dashboard-table-wrapper">
                                    <table className="table mb-0 institution-dashboard-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Username</th>
                                                <th>Email</th>
                                                <th>Status</th>
                                                <th>Created At</th>
                                                <th>Updated At</th>
                                                <th className="text-end" style={{ minWidth: '220px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4">Loading student profiles...</td>
                                                </tr>
                                            ) : profiles.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4">No student profiles found.</td>
                                                </tr>
                                            ) : (
                                                profiles.map((profile) => (
                                                    <tr key={profile.id}>
                                                        <td data-label="ID">{profile.id}</td>
                                                        <td data-label="Username">{profile.username}</td>
                                                        <td data-label="Email">{profile.email}</td>
                                                        <td data-label="Status">
                                                            <span className={getBadgeClass(profile.confirmation_status)}>
                                                                {profile.confirmation_status}
                                                            </span>
                                                        </td>
                                                        <td data-label="Created At">{profile.created_at ? new Date(profile.created_at).toLocaleString() : '-'}</td>
                                                        <td data-label="Updated At">{profile.updated_at ? new Date(profile.updated_at).toLocaleString() : '-'}</td>
                                                        <td data-label="Actions" className="text-end institution-dashboard-actions-cell">
                                                            <div className="institution-dashboard-action-group">
                                                                <button
                                                                    className="btn btn-success btn-sm institution-dashboard-action-btn"
                                                                    onClick={() => handleStatusUpdate(profile.id, 'approved')}
                                                                    disabled={autoApproving || actionLoadingId === profile.id || profile.confirmation_status === 'approved'}
                                                                >
                                                                    Approve
                                                                </button>
                                                                <button
                                                                    className="btn btn-danger btn-sm institution-dashboard-action-btn"
                                                                    onClick={() => handleStatusUpdate(profile.id, 'rejected')}
                                                                    disabled={autoApproving || actionLoadingId === profile.id || profile.confirmation_status === 'rejected'}
                                                                >
                                                                    Reject
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-12">
                            <div className="login-right-form">
                                <div className="login-top">
                                    <h3>Assigned Quizzes</h3>
                                    <p>Quizzes currently assigned to students registered under this institution.</p>
                                </div>
                                {assignedLoading ? (
                                    <div className="text-center py-4">Loading assigned quizzes...</div>
                                ) : assignedQuizzes.length === 0 ? (
                                    <div className="text-center py-4">No quizzes assigned yet.</div>
                                ) : (
                                    <div className="row g-3">
                                        {assignedQuizzes.map((quiz) => (
                                            <div className="col-md-6 col-xl-4" key={quiz.id}>
                                                <div className="card h-100 border">
                                                    <div className="card-body d-flex flex-column">
                                                        <h5 className="card-title mb-3">{quiz.test_name || `Quiz #${quiz.test_id}`}</h5>
                                                        <p className="mb-2"><strong>Selected:</strong> {quiz.selected_question_count}</p>
                                                        <p className="mb-2"><strong>Total Marks:</strong> {quiz.total_marks}</p>
                                                        <p className="mb-3"><strong>Assigned At:</strong> {quiz.assigned_at ? new Date(quiz.assigned_at).toLocaleString() : '-'}</p>
                                                        <div className="mt-auto pt-2">
                                                            <Link
                                                                to={`/dashboard/monitoring/${quiz.test_id}`}
                                                                className="btn btn-primary w-100 mb-2"
                                                            >
                                                                Monitor Live
                                                            </Link>
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger w-100"
                                                                onClick={() => handleUnassignQuiz(quiz.test_id)}
                                                                disabled={actionLoadingId === quiz.test_id}
                                                            >
                                                                {actionLoadingId === quiz.test_id ? 'Deleting...' : 'Delete Assigned Quiz'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-12">
                            <div className="login-right-form">
                                <div className="login-top">
                                    <h3>Tests Created by Admin</h3>
                                    <p>Only unassigned quizzes are shown here (for your institution).</p>
                                </div>
                                {testsLoading ? (
                                    <div className="text-center py-4">Loading tests...</div>
                                ) : availableTests.length === 0 ? (
                                    <div className="text-center py-4">No unassigned tests available.</div>
                                ) : (
                                    <div className="row g-3">
                                        {availableTests.map((test) => (
                                            <div className="col-md-6 col-xl-4" key={test.id}>
                                                <div className="card h-100 border">
                                                    <div className="card-body d-flex flex-column">
                                                        <h5 className="card-title mb-3">{test.name || `Quiz #${test.id}`}</h5>
                                                        <p className="mb-2"><strong>Requested:</strong> {test.requested_question_count}</p>
                                                        <p className="mb-2"><strong>Selected:</strong> {test.selected_question_count}</p>
                                                        <p className="mb-2"><strong>Total Marks:</strong> {test.total_marks}</p>
                                                        <p className="mb-3"><strong>Started At:</strong> {test.created_at ? new Date(test.created_at).toLocaleString() : '-'}</p>
                                                        <div className="mt-auto pt-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary w-100"
                                                                onClick={() => handleAssignQuiz(test.id)}
                                                                disabled={actionLoadingId === test.id}
                                                            >
                                                                {actionLoadingId === test.id ? 'Assigning...' : 'Assign Quiz'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </>
    );
};

export default InstitutionDashboardMain;
